class Accordion extends HTMLElement {

    constructor() {
        super()
        this.init()
    }

    private init() {
        this.initClickEvent()
        this.activeFirstItem()
        this.handleHashNavigation()
        this.observeNestedAccordions()
    }

    private observeNestedAccordions() {
        // Listen for custom events from nested accordions
        this.addEventListener('accordion-changed', () => {
            // Update multiple times during the animation for smooth height adjustment
            this.updateParentHeights(); // Immediate update
            
            // Update during animation at key frames
            requestAnimationFrame(() => {
                this.updateParentHeights();
                
                setTimeout(() => {
                    this.updateParentHeights();
                }, 100);
                
                setTimeout(() => {
                    this.updateParentHeights();
                }, 200);
                
                setTimeout(() => {
                    this.updateParentHeights();
                }, 400); // Final update after transition completes
            });
        });
    }

    private initClickEvent() {
        const accordionList = this.querySelector(':scope > .accordion-js-list');

        if (!accordionList) {return}

        accordionList.addEventListener('click', (event) => {
            const target = (event.target instanceof Element)
                ? event.target.closest('.accordion-js-title') as HTMLButtonElement | null
                : null;
            if (target && target.classList.contains('accordion-js-title')) {
                const parent = target.closest('.accordion-js-item');
                // Check if this item belongs to this accordion (not a nested one)
                if (parent && parent.parentElement === accordionList) {
                    const content = parent?.querySelector('.accordion-js-content') as HTMLElement | null;
                    if (content) {
                        content.classList.contains('active') ? this.close(target, content) : this.open(target, content);
                    }
                }
            }
        });
    }

    private activeFirstItem() {
        if ( this.dataset.openFirstItem !== 'true') {return}

        const accordionList = this.querySelector(':scope > .accordion-js-list');
        if (!accordionList) {return}
        
        const buttons = accordionList.querySelectorAll(':scope > .accordion-js-item > .accordion-js-title')
        const dropdowns = accordionList.querySelectorAll(':scope > .accordion-js-item > .accordion-js-content')

        const buttonActive = <HTMLButtonElement>buttons[0]
        const dropDownActive = <HTMLElement>dropdowns[0]

        this.open(buttonActive, dropDownActive)
    }
    
    private open (button: HTMLElement, dropDown: HTMLElement) {
        this.closeAllDrops(button, dropDown);
        dropDown.style.height = dropDown.scrollHeight + 'px';
        button.classList.add('active');
        dropDown.classList.add('active');
        
        // Notify parent accordions immediately
        this.dispatchChangeEvent();
        
        // Also update immediately for this accordion's parents
        requestAnimationFrame(() => {
            this.updateParentHeights();
        });
    }

    private close (button: HTMLElement, dropDown: HTMLElement) {
        button.classList.remove('active');
        dropDown.classList.remove('active');
        dropDown.style.height = '';
        
        // Notify parent accordions immediately
        this.dispatchChangeEvent();
        
        // Also update immediately for this accordion's parents
        requestAnimationFrame(() => {
            this.updateParentHeights();
        });
    }

    private dispatchChangeEvent() {
        // Dispatch custom event that bubbles up to parent accordions
        const event = new CustomEvent('accordion-changed', { bubbles: true });
        this.dispatchEvent(event);
    }

    private updateParentHeights() {
        // Find parent accordion elements and update their heights
        let parentAccordion = this.parentElement?.closest('accordion-list') as Accordion | null;
        
        while (parentAccordion) {
            const parentContent = parentAccordion.querySelector(':scope > .accordion-js-list > .accordion-js-item > .accordion-js-content.active') as HTMLElement;
            
            if (parentContent) {
                // Save current transition
                const originalTransition = parentContent.style.transition;
                
                // Temporarily disable transition to measure
                parentContent.style.transition = 'none';
                const currentHeight = parentContent.style.height;
                
                // Get the natural height
                parentContent.style.height = 'auto';
                const newHeight = parentContent.scrollHeight;
                
                // Restore the current height without visual change
                parentContent.style.height = currentHeight;
                
                // Force reflow
                parentContent.offsetHeight;
                
                // Re-enable transition
                parentContent.style.transition = originalTransition;
                
                // Now animate to the new height
                requestAnimationFrame(() => {
                    parentContent.style.height = newHeight + 'px';
                });
            }
            
            // Move up to the next parent accordion
            parentAccordion = parentAccordion.parentElement?.closest('accordion-list') as Accordion | null;
        }
    }

    private closeAllDrops (button: HTMLElement, dropDown: HTMLElement) {
        const accordionList = this.querySelector(':scope > .accordion-js-list');
        if (!accordionList) {return}
        
        const accordionListItems = accordionList.querySelectorAll(':scope > .accordion-js-item')
        accordionListItems.forEach((elem) => {
            const btn = <HTMLElement>elem.children[0]
            const drop = <HTMLElement>elem.children[1]
            if (btn !== button && drop !== dropDown) {
                this.close(btn, drop);
            }
        })
    }

    private handleHashNavigation() {
        // Check if there's a hash in the URL
        const hash = window.location.hash;
        if (!hash) { return; }

        // Parse hash for format: #main-id?open=item-id
        const hashParts = hash.substring(1).split('?');
        const mainId = hashParts[0];
        const params = new URLSearchParams(hashParts[1] || '');
        const openItemId = params.get('open');
        
        // If this accordion is the target and has an item to open
        if (mainId === this.id && openItemId) {
            const targetItem = this.querySelector(`#${openItemId}.accordion-js-item`);
            
            if (targetItem) {
                const button = targetItem.querySelector('.accordion-js-title') as HTMLElement;
                const content = targetItem.querySelector('.accordion-js-content') as HTMLElement;
                
                if (button && content) {
                    // Wait a bit for the page to fully load
                    setTimeout(() => {
                        // Open the accordion
                        this.open(button, content);
                        
                        // Check if there's a link inside the content and click it automatically
                        const link = content.querySelector('a') as HTMLAnchorElement | null;
                        if (link) {
                            // Click the link to open it
                            link.click();
                        }
                        
                        // Wait for accordion animation to complete before scrolling
                        setTimeout(() => {
                            // Scroll to the accordion with offset for fixed navbar
                            const navbarHeight = document.querySelector('.nav-wrapper')?.getBoundingClientRect().height || 80;
                            const elementPosition = this.getBoundingClientRect().top + window.scrollY;
                            window.scrollTo({ top: elementPosition - navbarHeight - 100, behavior: 'smooth' });
                        }, 400);
                    }, 100);
                }
            }
        }
    }
}

export default Accordion